/**
 * Service-category scheduling defaults.
 *
 *   companies/{companyId}/settings/serviceDefaults
 *     { roof: 'appointment', pest: 'route_flexible', ... }
 *
 * Single doc keyed by service category. Read in ScheduleJobModal /
 * QuoteDetail to pre-select the mode toggle for one-time (non-program)
 * jobs. Programs carry their own `defaultJobMode` on the program doc —
 * this map only covers the non-recurring path.
 *
 * Empty doc / missing category → callers must fall back to a sane default
 * (component-level — usually 'appointment' for first-time scheduling so
 * we never under-commit a customer).
 */
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

export const MODE_ROUTE = 'route_flexible';
export const MODE_APPT  = 'appointment';

// Canonical category list — mirrors PricingSettings + ServiceTemplates +
// ServicePrograms. Keep in sync; the Scheduling Defaults UI iterates this.
export const SERVICE_CATEGORIES = [
  { value: 'roof',        label: 'Roof' },
  { value: 'houseWash',   label: 'House Wash' },
  { value: 'siding',      label: 'Siding' },
  { value: 'gutter',      label: 'Gutter' },
  { value: 'driveway',    label: 'Driveway' },
  { value: 'deck',        label: 'Deck' },
  { value: 'fence',       label: 'Fence' },
  { value: 'pest',        label: 'Pest Control' },
  { value: 'lawn',        label: 'Lawn Care' },
  { value: 'snow',        label: 'Snow Removal' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'cleaning',    label: 'Cleaning' },
  { value: 'other',       label: 'Other' },
];

const VALID_MODES = new Set([MODE_ROUTE, MODE_APPT]);

function _ref(companyId) {
  return doc(db, 'companies', companyId, 'settings', 'serviceDefaults');
}

/**
 * One-shot read. Returns `{}` when the doc doesn't exist or any field is
 * malformed.
 */
export async function fetchServiceDefaults(companyId) {
  if (!companyId) return {};
  try {
    const snap = await getDoc(_ref(companyId));
    if (!snap.exists()) return {};
    const raw = snap.data() || {};
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
      if (VALID_MODES.has(v)) out[k] = v;
    }
    return out;
  } catch (err) {
    console.warn('[serviceDefaults] fetch failed:', err);
    return {};
  }
}

/**
 * Write or merge a partial map. Caller passes `{ roof: 'appointment' }`
 * — other keys are preserved by Firestore's merge.
 */
export async function saveServiceDefaults(companyId, partial) {
  if (!companyId || !partial || typeof partial !== 'object') return;
  const clean = {};
  for (const [k, v] of Object.entries(partial)) {
    if (VALID_MODES.has(v)) clean[k] = v;
  }
  await setDoc(_ref(companyId), clean, { merge: true });
}

/**
 * Realtime subscription. Returns an unsubscribe fn.
 */
export function subscribeServiceDefaults(companyId, cb) {
  if (!companyId) return () => {};
  return onSnapshot(
    _ref(companyId),
    (snap) => {
      const raw = snap.exists() ? snap.data() || {} : {};
      const out = {};
      for (const [k, v] of Object.entries(raw)) {
        if (VALID_MODES.has(v)) out[k] = v;
      }
      cb(out);
    },
    () => cb({}),
  );
}

/**
 * Resolve the default mode for a given lead. Categorises the lead's
 * `service` string against the known categories (case-insensitive,
 * substring match — 'roof_softwash' matches 'roof'). Falls back to
 * 'appointment' so we never silently book a route-flexible visit for
 * a customer-committed sale (the safer wrong default).
 */
export function resolveDefaultMode(defaults, serviceLabel, fallback = MODE_APPT) {
  if (!defaults || typeof defaults !== 'object') return fallback;
  if (!serviceLabel || typeof serviceLabel !== 'string') return fallback;
  const lower = serviceLabel.toLowerCase();
  // Direct hit (e.g. lead.service === 'roof')
  if (defaults[lower] && VALID_MODES.has(defaults[lower])) return defaults[lower];
  // Substring match against known categories
  for (const { value } of SERVICE_CATEGORIES) {
    if (lower.includes(value) && defaults[value] && VALID_MODES.has(defaults[value])) {
      return defaults[value];
    }
  }
  return fallback;
}
